import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { ParamPanel } from '@/components/planner/ParamPanel';
import { ChatInput } from '@/components/planner/ChatInput';
import { ResultPanel } from '@/components/planner/ResultPanel';
import { TextResultPanel } from '@/components/planner/TextResultPanel';
import { ActionBar } from '@/components/planner/ActionBar';
import { useAuth } from '@/hooks/useAuth';
import { useTrips } from '@/hooks/useTrips';
import { generateTrip } from '@/lib/aiService';
import { Sparkles, Wand2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import OceanBackground from '@/components/background/OceanBackground';
const Planner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { createMutation } = useTrips(user?.id);

  const [params, setParams] = useState({
    destination: '',

    days: 3, people: 2, budget: 3000, pace: 'moderate', transport: 'public', preference: 'sightseeing', special: '',
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);

  useEffect(() => {
    const template = location.state?.template;
    if (template) setParams((prev) => ({ ...prev, preference: template }));
  }, [location]);

  const handleGenerate = async (message = '') => {
    if (!params.destination?.trim()) { toast.error('请输入目的地'); return; }
    setLoading(true);
    setChatMessages((prev) => [...prev, { role: 'user', content: message || '生成行程' }]);
    try {
      const tripResult = await generateTrip(params, (data) => setResult(data));
      setChatMessages((prev) => [...prev, { role: 'assistant', content: '行程已生成' }]);
      toast.success('行程生成成功');
    } catch (err) {
      toast.error(err.message || '生成失败');
      setChatMessages((prev) => [...prev, { role: 'assistant', content: '生成失败，请重试' }]);
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!user) { toast.error('请先登录'); navigate('/login'); return; }
    if (!result) { toast.error('请先生成行程'); return; }
    try {
      await createMutation.mutateAsync({
        user_id: user.id, title: result.title || `${params.destination}旅行计划`,
        destination: params.destination, days: params.days, budget: String(params.budget),
        style: params.preference, content: result, is_favorite: false,
      });
      toast.success('行程已保存'); navigate('/profile');
    } catch (err) { toast.error(err.message || '保存失败'); }
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, rgba(16,26,48,0.82) 0%, rgba(22,37,68,0.78) 50%, rgba(16,26,48,0.82) 100%)' }}>
      <Navbar />
      <OceanBackground />
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20 relative z-10">
        <div className="text-center mb-10" style={{ animation: 'rise .75s ease-out both' }}>
          <div className="ls ls-tl inline-flex items-center gap-1 mb-4"><Sparkles className="w-3 h-3" /> AI智能旅行规划</div>
          <h1 className="text-3xl md:text-4xl font-bold cb mb-3 is">定制你的专属旅程</h1>
          <p className="cm max-w-md mx-auto">输入旅行参数，AI将为你生成详细的行程攻略</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <ParamPanel params={params} setParams={setParams} onGenerate={handleGenerate} loading={loading} />
          </div>
          <div className="lg:col-span-2 space-y-6">
            {!result && !loading && (
              <div className="frost p-12 text-center" style={{ animation: 'rise .75s ease-out 0.1s both' }}>
                <div className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(135deg, var(--glow), var(--mid))', boxShadow: '0 8px 24px rgba(61,130,176,0.3)' }}>
                  <Wand2 className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold cb mb-2">准备开始规划</h3>
                <p className="cm">填写左侧参数后，点击 <span className="cg">"生成行程"</span> 按钮</p>
              </div>
            )}
            {loading && (
              <div className="frost p-12 text-center" style={{ animation: 'rise .75s ease-out 0.1s both' }}>
                <div className="flex flex-col items-center gap-4">
                  <div className="w-14 h-14 border-3 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'rgba(106,179,214,0.3)', borderTopColor: 'var(--bloom)' }} />
                  <p className="cm">AI正在为你规划行程...</p>
                </div>
              </div>
            )}
            {result && (
              <>
                {result.rawContent ? <TextResultPanel content={result.rawContent} /> : <ResultPanel result={result} />}
                <ActionBar onSave={handleSave} result={result} />
                <ChatInput onSend={handleGenerate} loading={loading} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Planner;
